<template>
  <div
    class="box flex h-full w-full flex-row divide-x divide-slate-200 overflow-hidden shadow-xl"
  >
    <div class="flex h-full w-64 flex-col">
      <InvoicingLeftPanel v-model:invoice="invoice" />
    </div>
    <div class="flex flex-1 flex-col divide-y">
      <div class="flex shrink-0 flex-row p-3">
        <div class="flex-1">
          <h3 class="text-2xl font-bold capitalize-first">
            {{ $t('Taxes') }}
          </h3>
        </div>
        <div class="flex shrink-0 gap-x-1">
          <BaseButton
            :icon="'chevron-left'"
            :title="$t('button.back')"
            inverse-icon-order
            size="small"
            @click="
              $router.push({
                name: 'invoice_info_route',
                params: {
                  invoice_id: invoice.id
                }
              })
            "
          />
        </div>
      </div>
      <div class="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
        <div
          v-if="!residenceKnown"
          class="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <FontAwesomeIcon
            class="mt-0.5 shrink-0"
            :icon="['fas', 'triangle-exclamation']"
          />
          {{ $t('global.wiki_item_residence_unknown_default_price') }}
        </div>
        <div
          v-for="(group, index) in itemsWiki"
          :key="
            group.parent_id ? `${group.parent_type}:${group.parent_id}` : index
          "
          class="flex flex-col gap-4 squircle-lg rounded border p-4 shadow-sm"
        >
          <div
            class="flex flex-row items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 dark:border-orange-700 dark:bg-orange-900/20"
          >
            <div class="flex min-w-0 flex-1 flex-col gap-1.5">
              <div class="flex items-center gap-2">
                <div
                  class="flex aspect-square w-6 shrink-0 items-center justify-center rounded-md bg-orange-300 text-sm text-contrast-secondary shadow-sm"
                >
                  <FontAwesomeIcon :icon="['fal', 'sitemap']" />
                </div>
                <h3 class="text-lg leading-tight font-bold capitalize-first">
                  {{ groupTitle(group) }}
                </h3>
              </div>
              <div
                v-if="groupEvents(group).length"
                class="flex flex-wrap gap-1.5 pl-8"
              >
                <span
                  v-for="event in groupEvents(group)"
                  :key="event"
                  class="rounded-full border border-orange-200 bg-white px-2.5 py-0.5 text-xs font-medium text-orange-800 dark:border-orange-700 dark:bg-slate-700 dark:text-orange-200"
                >
                  {{ event }}
                </span>
              </div>
            </div>
            <button
              v-if="selectableItems(group).length"
              type="button"
              class="flex shrink-0 items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-primary hover:bg-white/70"
              @click="toggleGroup(index, group)"
            >
              <FontAwesomeIcon
                :icon="[
                  'fal',
                  allSelected(index, group) ? 'square' : 'check-square'
                ]"
              />
              {{
                allSelected(index, group)
                  ? $t('button.unselect_all')
                  : $t('button.select_all')
              }}
            </button>
          </div>
          <div class="flex flex-1 flex-col overflow-y-auto">
            <div
              class="grid auto-cols-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
            >
              <ItemWikiCard
                v-for="wikiItem in group.items"
                :key="wikiItem.id"
                :wiki-item="wikiItem"
                :selected="isSelected(index, wikiItem.id)"
                @click="() => toggleItem(index, wikiItem.id)"
                @refresh-wiki-items="refreshList"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex shrink-0 flex-row justify-end p-3">
        <BaseButton
          :title="$t('button.add')"
          icon="plus"
          :disabled="selectedIds.length === 0"
          @click="saveInvoiceItems"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import InvoicingLeftPanel from '@/views/invoicing/show/tabs/information/components/information/InvoicingLeftPanel.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ItemWikiCard from '@/views/invoicing/show/tabs/automation/component/ItemWikiCard.vue'

export default {
  name: 'TheWikiItemsSelector',
  components: {
    ItemWikiCard,
    FontAwesomeIcon,
    InvoicingLeftPanel,
    BaseButton
  },
  props: {
    invoicing_id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      itemsWiki: [],
      residenceKnown: true,
      invoiceItems: [],
      selectAllButton: false,
      // `${group index}:${wiki item id}`: the same wiki item can sit in several groups
      selectedKeys: []
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice'
    }),
    selectedKeySet() {
      return new Set(this.selectedKeys)
    },
    selectedIds() {
      return [
        ...new Set(
          this.selectedKeys.map((key) => key.slice(key.indexOf(':') + 1))
        )
      ]
    }
  },
  mounted() {
    this.fetchItems()
  },
  methods: {
    ...mapActions({
      processAddItemDossiersToInvoiceAction:
        'invoicing/addItemDossiersToInvoice',
      processFetchWikiItemsAction: 'invoicing/fetchWikiItems',
      processAddWikiItemsToInvoiceAction: 'invoicing/addWikiItemsToInvoice'
    }),
    refreshList() {
      this.fetchItems()
    },
    fetchItems() {
      this.processFetchWikiItemsAction({
        invoice_id: this.invoicing_id
      }).then((response) => {
        this.itemsWiki = response.wiki_items
        this.residenceKnown = response.residence_known !== false
        this.invoiceItems =
          this.invoice?.invoice_items?.map(
            (invoice_item) => invoice_item.wiki_item_id
          ) ?? []

        this.selectedKeys = []
        this.itemsWiki.forEach((group, index) => {
          this.selectableItems(group).forEach((item) => {
            this.selectedKeys.push(this.selectionKey(index, item.id))
          })
        })
      })
    },
    /** A group is a city, or a place of a city: its items are listed once, whatever the events using it. */
    groupTitle(group) {
      // Former API format: one group per event, with no owner of the items
      if (!group.parent_type) {
        return [group.type, group.city_name, group.place_name]
          .filter(Boolean)
          .join(' - ')
      }

      return group.place_name
        ? [group.place_name, group.city_name && `(${group.city_name})`]
            .filter(Boolean)
            .join(' ')
        : (group.city_name ?? '')
    },
    groupEvents(group) {
      return (group.events ?? []).map((event) =>
        [event.label, event.place_name].filter(Boolean).join(' - ')
      )
    },
    /** Only an item linked to an internal item, not yet on the invoice, can be added */
    isSelectable(item) {
      return !!item.item_wiki_item_id && !this.invoiceItems.includes(item.id)
    },
    selectableItems(group) {
      return group.items.filter((item) => this.isSelectable(item))
    },
    selectionKey(groupIndex, itemId) {
      return `${groupIndex}:${itemId}`
    },
    isSelected(groupIndex, itemId) {
      return this.selectedKeySet.has(this.selectionKey(groupIndex, itemId))
    },
    allSelected(groupIndex, group) {
      return this.selectableItems(group).every((item) =>
        this.isSelected(groupIndex, item.id)
      )
    },
    toggleGroup(groupIndex, group) {
      const keys = this.selectableItems(group).map((item) =>
        this.selectionKey(groupIndex, item.id)
      )

      this.selectedKeys = this.allSelected(groupIndex, group)
        ? this.selectedKeys.filter((key) => !keys.includes(key))
        : [...new Set([...this.selectedKeys, ...keys])]
    },
    toggleItem(groupIndex, itemId) {
      const key = this.selectionKey(groupIndex, itemId)

      this.selectedKeys = this.selectedKeys.includes(key)
        ? this.selectedKeys.filter((selected) => selected !== key)
        : [...this.selectedKeys, key]
    },
    saveInvoiceItems() {
      const payload = {
        id: this.invoice.id,
        items: this.selectedIds
      }

      this.processAddWikiItemsToInvoiceAction(payload).then((response) => {
        this.$h.toastSuccess(this.$t('toasts.items_added_to_invoice'))
        this.$router.push({
          name: 'invoice_info_route',
          params: {
            invoice_id: this.invoicing_id
          }
        })
      })
    }
  }
}
</script>
